// src/App.jsx
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { Box } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProviderCustom, useThemeMode } from './store/themeStore';
import { useAuth } from './store/authStore';
import { getCurrentLanguage } from './utils/navigation';
import './i18n';

// Auth Components
import LoginForm from './components/auth/LoginForm';
import RegisterForm from './components/auth/RegisterForm';
import RoleSelectionPage from './pages/auth/RoleSelectionPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';

// Student Components
import StudentDashboard from './pages/student/Dashboard';
import LiveClasses from './pages/student/LiveClasses';
import AssignmentsPage from './pages/student/AssignmentsPage';
import SchedulePage from './pages/student/SchedulePage';
import StudentRegistration from './pages/student/StudentRegistration';
import AssignmentDetails from './pages/student/AssignmentDetails';
import StudentProfile from './pages/student/Profile';
import FreeUserNotice from './pages/student/FreeUserNotice';
import SearchTeachers from './pages/student/SearchTeachers';
import TeacherProfile from './pages/student/TeacherProfile';
import BookLesson from './pages/student/BookLesson';
import MyLessons from './pages/student/MyLessons';
import StudentMessages from './pages/student/Messages';
import PaymentForm from './pages/student/PaymentForm';
import VideoCall from './pages/shared/VideoCall';

// Teacher Components
import TeacherLayout from './pages/teacher/TeacherLayout';
import TeacherHome from './pages/teacher/NewDashboard';
import TeacherRegistration from './pages/teacher/Registration';
import TeacherAssignments from './pages/teacher/Assignments';
import TeacherAssignmentDetails from './pages/teacher/AssignmentDetails';
import TeacherMessages from './pages/teacher/Messages';
import TeacherCalendar from './pages/teacher/TeacherCalendar';
import TeacherStudents from './pages/teacher/Students';
import TeacherEarnings from './pages/teacher/Earnings';
import TeacherSettings from './pages/teacher/TeacherSettings';
import TeacherProfileView from './pages/teacher/TeacherProfileView';
import LessonPosts from './pages/teacher/LessonPosts';

// Admin Components
import AdminDashboard from './pages/admin/Dashboard';
import TeacherApproval from './pages/admin/TeacherApproval';

// Layout Components
import Navbar from './components/layout/Navbar';

const AppRoutes = () => {
  const PrivateRoute = ({ children }) => {
    const { user, isAuthenticated, loading } = useAuth();
    const lang = getCurrentLanguage();

    if (loading) return <div>Loading...</div>;
    if (!isAuthenticated) return <Navigate to={`/${lang}/auth/login`} replace />;

    const path = window.location.pathname;
    const basePath = `/${lang}/${user?.role || 'student'}`;
    const isCorrectRole = path.startsWith(basePath);

    if (!isCorrectRole) {
      switch (user?.role) {
        case 'admin':
          return <Navigate to={`/${lang}/admin/dashboard`} replace />;
        case 'teacher':
          return <Navigate to={`/${lang}/teacher/dashboard`} replace />;
        default:
          return <Navigate to={`/${lang}/student/dashboard`} replace />;
      }
    }

    return children;
  };

  const ProtectedLayout = () => (
    <>
      <Navbar />
      <Outlet />
    </>
  );

  const LangIndexRedirect = () => {
    const { user, isAuthenticated, loading } = useAuth();
    const lang = getCurrentLanguage();
    if (loading) return <div>Loading...</div>;
    if (!isAuthenticated) return <Navigate to={`/${lang}/auth/login`} replace />;

    switch (user?.role) {
      case 'admin':
        return <Navigate to={`/${lang}/admin/dashboard`} replace />;
      case 'teacher':
        return <Navigate to={`/${lang}/teacher/dashboard`} replace />;
      default:
        return <Navigate to={`/${lang}/student/dashboard`} replace />;
    }
  };

  const TeacherProtectedLayout = () => (
    <>
      <Navbar />
      <TeacherLayout />
    </>
  );

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/en/auth/login" replace />} />
      <Route path="/:lang">
        <Route index element={<LangIndexRedirect />} />

        {/* Public Routes */}
        <Route path="auth/login" element={<LoginForm />} />
        <Route path="auth/register" element={<RegisterForm />} />
        <Route path="auth/role-selection" element={<RoleSelectionPage />} />
        <Route path="auth/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="auth/reset-password" element={<ResetPasswordPage />} />
        <Route path="teacher/assignments" element={
          <Box sx={{ bgcolor: '#FAFAFA', minHeight: '100vh' }}>
            <Navbar />
            <TeacherAssignments />
          </Box>
        } />
        <Route path="teacher/assignments/:id" element={<TeacherAssignmentDetails />} />

        {/* Admin Routes */}
        <Route path="admin" element={<PrivateRoute><ProtectedLayout /></PrivateRoute>}>
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="teacher-approval" element={<TeacherApproval />} />
        </Route>

        {/* Standalone Video Call routes (no navbar/layout) */}
        <Route path="student/call/:roomId" element={<PrivateRoute><VideoCall /></PrivateRoute>} />
        <Route path="teacher/call/:roomId" element={<PrivateRoute><VideoCall /></PrivateRoute>} />

        {/* Student Routes */}
        <Route path="student" element={<PrivateRoute><ProtectedLayout /></PrivateRoute>}>
          <Route path="dashboard" element={<StudentDashboard />} />
          <Route path="search" element={<SearchTeachers />} />
          <Route path="teacher/:teacherId" element={<TeacherProfile />} />
          <Route path="book/:teacherId" element={<BookLesson />} />
          <Route path="my-lessons" element={<MyLessons />} />
          <Route path="messages" element={<StudentMessages />} />
          <Route path="pay/:teacherId" element={<PaymentForm />} />
          <Route path="live-classes" element={<LiveClasses />} />
          <Route path="assignments" element={<AssignmentsPage />} />
          <Route path="assignments/:id" element={<AssignmentDetails />} />
          <Route path="schedule" element={<SchedulePage />} />
          <Route path="register" element={<StudentRegistration />} />
          <Route path="profile" element={<StudentProfile />} />
          <Route path="free-notice" element={<FreeUserNotice />} />
        </Route>

        {/* Teacher Routes */}
        <Route path="teacher" element={<PrivateRoute><TeacherProtectedLayout /></PrivateRoute>}>
          <Route path="dashboard" element={<TeacherHome />} />
          <Route path="register" element={<TeacherRegistration />} />
          <Route path="lessons" element={<LessonPosts />} />
          <Route path="messages" element={<TeacherMessages />} />
          <Route path="calendar" element={<TeacherCalendar />} />
          <Route path="students" element={<TeacherStudents />} />
          <Route path="earnings" element={<TeacherEarnings />} />
          <Route path="profile" element={<TeacherProfileView />} />
          <Route path="settings" element={<TeacherSettings />} />
        </Route>

        {/* Default Route */}
        <Route path="*" element={<Navigate to="/en/auth/login" replace />} />
      </Route>
    </Routes>
  );
}

const AppWithTheme = () => {
  const { theme } = useThemeMode();
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppRoutes />
    </ThemeProvider>
  );
};

const App = () => {
  return (
    <Router>
      <ThemeProviderCustom>
        <AppWithTheme />
      </ThemeProviderCustom>
    </Router>
  );
};

export default App;
